import React from 'react';
import {
  Card,
  SimpleGrid,
  Text,
  Group,
  Badge,
  Stack,
  Loader,
  Avatar,
  Paper,
  ThemeIcon,
  Box,
} from '@mantine/core';
import {
  IconClockHour4,
  IconCircleCheck,
  IconCircleX,
  IconPercentage,
  IconHourglassHigh,
  IconAlertTriangle,
  IconUserQuestion,
  IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useReviewStats } from '../../hooks/useApi';

const DOMAIN_COLORS: Record<string, string> = {
  technical: 'blue',
  qa: 'grape',
  devops: 'orange',
  product: 'teal',
  soft: 'pink',
};

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  color: string;
  icon: React.ReactNode;
  emphasize?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, icon, emphasize }) => (
  <Paper withBorder radius="md" p="md" className="review-card">
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {label}
        </Text>
        <Text fw={700} size="xl" c={emphasize ? color : undefined}>
          {value}
        </Text>
      </Stack>
      <ThemeIcon variant="light" color={color} radius="md" size={38}>
        {icon}
      </ThemeIcon>
    </Group>
  </Paper>
);

/** Admin dashboard: aggregate metrics for the competency review flow. */
export const ReviewStatsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useReviewStats();

  if (isLoading) {
    return (
      <Card withBorder p="md" mb="md" radius="md">
        <Group justify="center">
          <Loader size="sm" />
        </Group>
      </Card>
    );
  }
  if (!data) return null;

  const fmtCycle = (h: number | null) => {
    if (h == null) return '—';
    return h < 24
      ? `${h.toFixed(1)} ${t('reviewRequests.stats.hours')}`
      : `${(h / 24).toFixed(1)} ${t('reviewRequests.stats.days')}`;
  };
  const rate = data.approvalRate == null ? '—' : `${Math.round(data.approvalRate * 100)}%`;
  const ageDays = (iso: string) =>
    Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));

  return (
    <Stack gap="md" mb="xl">
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <StatCard
          label={t('reviewRequests.stats.pending')}
          value={data.counts.pending}
          color="blue"
          icon={<IconClockHour4 size={20} />}
        />
        <StatCard
          label={t('reviewRequests.stats.approved')}
          value={data.counts.approved}
          color="teal"
          icon={<IconCircleCheck size={20} />}
        />
        <StatCard
          label={t('reviewRequests.stats.rejected')}
          value={data.counts.rejected}
          color="red"
          icon={<IconCircleX size={20} />}
        />
        <StatCard
          label={t('reviewRequests.stats.approvalRate')}
          value={rate}
          color="grape"
          icon={<IconPercentage size={20} />}
        />
        <StatCard
          label={t('reviewRequests.stats.avgCycle')}
          value={fmtCycle(data.avgCycleHours)}
          color="indigo"
          icon={<IconHourglassHigh size={20} />}
        />
        <StatCard
          label={t('reviewRequests.stats.stalled', { days: data.staleDays })}
          value={data.stalledCount}
          color="orange"
          icon={<IconAlertTriangle size={20} />}
          emphasize={data.stalledCount > 0}
        />
        <StatCard
          label={t('reviewRequests.stats.needsManual')}
          value={data.needsManualCount}
          color="orange"
          icon={<IconUserQuestion size={20} />}
          emphasize={data.needsManualCount > 0}
        />
        <StatCard
          label={t('reviewRequests.stats.avgReviewers')}
          value={data.avgRequired.toFixed(1)}
          color="cyan"
          icon={<IconUsers size={20} />}
        />
      </SimpleGrid>

      <Card withBorder p="md" radius="md">
        <Text size="sm" fw={600} mb="sm">
          {t('reviewRequests.stats.byArea')}
        </Text>
        <Group gap="lg" wrap="wrap">
          {Object.entries(data.byDomain).map(([domain, n]) => (
            <Group gap={8} key={domain} wrap="nowrap">
              <Box
                w={10}
                h={10}
                style={{
                  borderRadius: '50%',
                  background: `var(--mantine-color-${DOMAIN_COLORS[domain] || 'gray'}-6)`,
                }}
              />
              <Text size="sm" c="dimmed">
                {t(`competencyMaps.domains.${domain}`)}
              </Text>
              <Text size="sm" fw={700}>
                {n}
              </Text>
            </Group>
          ))}
        </Group>
      </Card>

      {data.oldestPending.length > 0 && (
        <Card withBorder p="md" radius="md">
          <Text size="sm" fw={600} mb="sm">
            {t('reviewRequests.stats.oldestPending')}
          </Text>
          <Stack gap="xs">
            {data.oldestPending.map((p) => {
              const isStale = ageDays(p.createdAt) >= data.staleDays;
              return (
                <Paper
                  key={p._id}
                  withBorder
                  radius="md"
                  p="sm"
                  className="review-card"
                  style={{
                    borderLeft: `3px solid var(--mantine-color-${isStale ? 'orange' : 'yellow'}-6)`,
                  }}
                >
                  <Group justify="space-between" wrap="nowrap" gap="md">
                    <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <Avatar src={p.applicant?.avatar} size="sm" radius="xl">
                        {(p.applicant?.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Stack gap={2} style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {p.competencyTitle}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {p.applicant?.name || '—'}
                        </Text>
                      </Stack>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      {p.total > 0 && (
                        <Badge variant="light" color="blue" radius="sm">
                          {p.approved}/{p.total}
                        </Badge>
                      )}
                      {p.needsManualReviewer && (
                        <Badge variant="light" color="orange" radius="sm">
                          {t('reviewRequests.needsManual')}
                        </Badge>
                      )}
                      <Badge variant="light" color={isStale ? 'orange' : 'gray'} radius="sm">
                        {t('reviewRequests.stats.ageDays', { count: ageDays(p.createdAt) })}
                      </Badge>
                    </Group>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
